import { useEffect, useRef } from "react";

/**
 * Erkennt Eingaben eines USB-HID-Barcode-Scanners.
 * Ein typischer HID-Scanner tippt Zeichen sehr schnell (< 30 ms zwischen Zeichen)
 * und schließt mit Enter ab. Manuelles Tippen ist meist langsamer.
 *
 * Optionen:
 *   enabled          — Global-Listener an/aus (default true)
 *   minLength        — mindestens erforderliche Zeichenlänge (default 4)
 *   avgIntervalMs    — max. durchschnittliches Intervall zwischen Zeichen (default 45 ms)
 *   ignoreInInputs   — bei true: nichts machen, wenn Fokus in input/textarea/contenteditable liegt
 *
 * onScan(code) wird ausgeführt, sobald ein Enter kommt und die Heuristik matched.
 */
export default function useBarcodeScanner(onScan, {
  enabled = true,
  minLength = 4,
  avgIntervalMs = 45,
  ignoreInInputs = true,
} = {}) {
  const bufferRef = useRef("");
  const timesRef = useRef([]);
  const onScanRef = useRef(onScan);
  useEffect(() => { onScanRef.current = onScan; }, [onScan]);

  useEffect(() => {
    if (!enabled) return undefined;

    const isTypingTarget = (el) => {
      if (!el) return false;
      const tag = (el.tagName || "").toLowerCase();
      if (tag === "input" || tag === "textarea" || tag === "select") return true;
      if (el.isContentEditable) return true;
      return false;
    };

    const reset = () => { bufferRef.current = ""; timesRef.current = []; };

    const onKeyDown = (e) => {
      if (e.ctrlKey || e.metaKey || e.altKey) return;
      const target = e.target;
      const inField = isTypingTarget(target);

      if (e.key === "Enter") {
        const buf = bufferRef.current;
        const times = timesRef.current;
        if (buf.length >= minLength && times.length >= 2) {
          const intervals = [];
          for (let i = 1; i < times.length; i++) intervals.push(times[i] - times[i - 1]);
          const avg = intervals.reduce((a, b) => a + b, 0) / intervals.length;
          if (avg <= avgIntervalMs) {
            e.preventDefault();
            if (inField && ignoreInInputs && target && "value" in target) {
              // Getippte Zeichen aus dem Feld entfernen (Scanner hat sie reingedrückt)
              try {
                const v = target.value || "";
                if (v.endsWith(buf)) target.value = v.slice(0, -buf.length);
              } catch { /* noop */ }
            }
            const code = buf;
            reset();
            try { onScanRef.current?.(code); } catch { /* noop */ }
            return;
          }
        }
        reset();
        return;
      }

      // Nur druckbare Zeichen puffern
      if (e.key.length !== 1) return;
      if (inField && ignoreInInputs) {
        // trotzdem puffern, damit wir Scanner erkennen können; wir korrigieren beim Enter
      }
      const now = performance.now();
      const times = timesRef.current;
      if (times.length && now - times[times.length - 1] > 300) reset();
      bufferRef.current += e.key;
      timesRef.current.push(now);
    };

    window.addEventListener("keydown", onKeyDown, true);
    return () => window.removeEventListener("keydown", onKeyDown, true);
  }, [enabled, minLength, avgIntervalMs, ignoreInInputs]);
}
